if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      getSelectedOptionValues() {
        const selectsRoot = this.variantSelectors || this;
        const values = [];

        selectsRoot.querySelectorAll('fieldset').forEach((fieldset) => {
          const checked = fieldset.querySelector('input:checked');
          if (checked && checked.value) {
            values.push(checked.value.trim());
          }
        });

        selectsRoot.querySelectorAll('select').forEach((select) => {
          if (select.value) {
            values.push(select.value.trim());
          }
        });

        return values;
      }

      findLocalVariant(selectedOptionValues) {
        try {
          const variantsEl = document.getElementById(`ProductVariantsData-${this.dataset.section}`) ||
                             document.getElementById(`ProductVariantsData-${this.sectionId}`) ||
                             this.querySelector('[id^="ProductVariantsData"]');
          if (!variantsEl) return null;
          const variants = JSON.parse(variantsEl.textContent);
          if (!Array.isArray(variants) || !variants.length) return null;

          const currentValues = this.getSelectedOptionValues();
          if (currentValues.length) {
            const found = variants.find((variant) => {
              if (!variant.options || !Array.isArray(variant.options)) return false;
              if (variant.options.length !== currentValues.length) return false;
              return currentValues.every((val, i) => {
                const opt = variant.options[i];
                return opt && (opt.toLowerCase() === val.toLowerCase() || opt === val);
              });
            }) || variants.find((variant) => {
              return currentValues.every((val) =>
                variant.options.some((opt) => opt && opt.toLowerCase() === val.toLowerCase())
              );
            });
            if (found) return found;
          }

          if (Array.isArray(selectedOptionValues) && selectedOptionValues.length) {
            const found = variants.find((variant) => {
              return selectedOptionValues.every((val) =>
                variant.options.some((opt) => opt == val || String(opt).toLowerCase() === String(val).toLowerCase())
              );
            });
            if (found) return found;
          }

          return null;
        } catch (e) {
          console.error('Error finding variant:', e);
          return null;
        }
      }

      updateButtonState(isAvailable) {
        const productForm = this.productForm;
        if (isAvailable) {
          productForm?.toggleSubmitButton(false);
          const btn = this.querySelector('.product-form__submit');
          if (btn) {
            btn.removeAttribute('disabled');
            btn.removeAttribute('aria-disabled');
            const span = btn.querySelector('span');
            if (span) span.textContent = window.variantStrings?.addToCart || 'Add to cart';
          }
          const dynamicCheckout = this.querySelector('.product-form__dynamic-checkout');
          if (dynamicCheckout) {
            dynamicCheckout.style.display = '';
            window?.Shopify?.PaymentButton?.init();
          }
        } else {
          productForm?.toggleSubmitButton(true, window.variantStrings?.soldOut || 'Sold out');
          const btn = this.querySelector('.product-form__submit');
          if (btn) {
            btn.setAttribute('disabled', 'disabled');
            btn.setAttribute('aria-disabled', 'true');
            const span = btn.querySelector('span');
            if (span) span.textContent = window.variantStrings?.soldOut || 'Sold out';
          }
          const dynamicCheckout = this.querySelector('.product-form__dynamic-checkout');
          if (dynamicCheckout) {
            dynamicCheckout.style.display = 'none';
          }
        }
      }

      updateStockUrgency(variantId, isAvailable) {
        const stockEl = document.getElementById(`StockUrgency-${this.dataset.section}`) || document.getElementById(`StockUrgency-${this.sectionId}`);
        if (!stockEl) return;

        let inventoryQty = 0;
        try {
          const invDataEl = document.getElementById(`VariantInventoryData-${this.dataset.section}`) || document.getElementById(`VariantInventoryData-${this.sectionId}`);
          if (invDataEl) {
            const invData = JSON.parse(invDataEl.textContent);
            if (invData && invData[variantId]) {
              inventoryQty = invData[variantId].qty;
            }
          }
        } catch (e) {}

        if (!isAvailable || inventoryQty <= 0) {
          stockEl.classList.add('hidden');
          stockEl.style.display = 'none';
        } else {
          stockEl.classList.remove('hidden');
          stockEl.style.display = '';
          const qtyEl = document.getElementById(`StockQuantity-${this.dataset.section}`) || document.getElementById(`StockQuantity-${this.sectionId}`);
          const unitEl = document.getElementById(`StockUnit-${this.dataset.section}`) || document.getElementById(`StockUnit-${this.sectionId}`);
          const progressEl = document.getElementById(`StockProgress-${this.dataset.section}`) || document.getElementById(`StockProgress-${this.sectionId}`);
          if (qtyEl) qtyEl.textContent = inventoryQty;
          if (unitEl) unitEl.textContent = inventoryQty === 1 ? 'ITEM' : 'ITEMS';
          if (progressEl) {
            const percent = Math.min(100, Math.max(10, Math.round((inventoryQty / 20) * 100)));
            progressEl.style.width = `${percent}%`;
          }
        }
      }

      scrollToMedia() {
        const mediaGalleryEl = document.getElementById(`MediaGallery-${this.dataset.section}`) || 
                               document.getElementById(`MediaGallery-${this.sectionId}`) ||
                               document.querySelector('.product__media-wrapper') ||
                               this.closest('.product')?.querySelector('.product__media-wrapper');
        if (mediaGalleryEl) {
          const rect = mediaGalleryEl.getBoundingClientRect();
          const targetTop = rect.top + window.scrollY - 20;
          window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
        }
      }

      updateSku(variant) {
        const skuNumberEl = document.getElementById(`SkuNumber-${this.dataset.section}`) || 
                            document.getElementById(`SkuNumber-${this.sectionId}`) ||
                            this.querySelector('[id^="SkuNumber"]');
        if (skuNumberEl) {
          const skuValue = variant?.sku || skuNumberEl.dataset.vendor || '';
          if (skuValue) {
            skuNumberEl.textContent = skuValue;
            skuNumberEl.classList.remove('hidden');
          } else {
            skuNumberEl.textContent = '';
            skuNumberEl.classList.add('hidden');
          }
        }

        const skuBlockEl = document.getElementById(`Sku-${this.dataset.section}`) || 
                           document.getElementById(`Sku-${this.sectionId}`) ||
                           this.querySelector('[id^="Sku-"]');
        if (skuBlockEl) {
          const skuVal = variant?.sku || '';
          if (skuVal) {
            const span = skuBlockEl.querySelector('.visually-hidden');
            skuBlockEl.innerHTML = (span ? span.outerHTML : '') + ' ' + skuVal;
            skuBlockEl.classList.remove('visibility-hidden', 'hidden');
          } else {
            skuBlockEl.classList.add('visibility-hidden');
          }
        }
      }

      formatMoney(cents, format) {
        if (typeof cents === 'string') {
          cents = cents.replace(/[^0-9]/g, '');
        }
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const formatString = format || window.moneyFormat || 'Rs. {{amount}}';

        function defaultOption(opt, def) {
          return typeof opt === 'undefined' ? def : opt;
        }

        function formatWithDelimiters(number, precision, thousands, decimal) {
          precision = defaultOption(precision, 2);
          thousands = defaultOption(thousands, ',');
          decimal = defaultOption(decimal, '.');

          if (isNaN(number) || number == null) {
            return '0';
          }

          number = (number / 100.0).toFixed(precision);
          const parts = number.split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
          const centsVal = parts[1] ? decimal + parts[1] : '';

          return dollars + centsVal;
        }

        let value = '';
        switch (formatString.match(placeholderRegex)?.[1]) {
          case 'amount':
            value = formatWithDelimiters(cents, 2);
            break;
          case 'amount_no_decimals':
            value = formatWithDelimiters(cents, 0);
            break;
          case 'amount_with_comma_separator':
            value = formatWithDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = formatWithDelimiters(cents, 0, '.', ',');
            break;
          case 'amount_with_space_separator':
            value = formatWithDelimiters(cents, 2, ' ', ',');
            break;
          case 'amount_no_decimals_with_space_separator':
            value = formatWithDelimiters(cents, 0, ' ', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = formatWithDelimiters(cents, 2, "'", '.');
            break;
          default:
            value = formatWithDelimiters(cents, 2);
            break;
        }

        return formatString.replace(placeholderRegex, value);
      }

      updatePrice(variant) {
        if (!variant || variant.price === undefined) return;
        const priceContainer = document.getElementById(`price-${this.dataset.section}`) ||
                               document.getElementById(`price-${this.sectionId}`) ||
                               this.querySelector('[id^="price-"]') ||
                               document.querySelector(`[id^="price-${this.dataset.section}"]`);
        if (!priceContainer) return;

        const priceEl = priceContainer.querySelector('.price') || priceContainer;
        const price = variant.price;
        const compareAtPrice = variant.compare_at_price;
        const isOnSale = compareAtPrice != null && Number(compareAtPrice) > Number(price);

        const formatStr = window.moneyFormat || 'Rs. {{amount}}';
        const formattedPrice = this.formatMoney(price, formatStr);
        const formattedCompare = isOnSale ? this.formatMoney(compareAtPrice, formatStr) : '';

        if (isOnSale) {
          priceEl.classList.add('price--on-sale');
          priceEl.classList.remove('price--sold-out');

          const salePriceEl = priceContainer.querySelector('.price__sale .price-item--sale');
          if (salePriceEl) salePriceEl.textContent = formattedPrice;

          const comparePriceEl = priceContainer.querySelector('.price__sale s.price-item--regular');
          if (comparePriceEl) comparePriceEl.textContent = formattedCompare;

          const discountBadge = priceContainer.querySelector('.price__badge-discount');
          const discountPct = Math.round(((compareAtPrice - price) * 100) / compareAtPrice);
          if (discountBadge) {
            if (discountPct > 0) {
              discountBadge.textContent = `${discountPct}% OFF`;
              discountBadge.style.display = '';
            } else {
              discountBadge.style.display = 'none';
            }
          }
        } else {
          priceEl.classList.remove('price--on-sale');
          const regularPriceEl = priceContainer.querySelector('.price__regular .price-item--regular');
          if (regularPriceEl) regularPriceEl.textContent = formattedPrice;
        }

        if (variant.available === false) {
          priceEl.classList.add('price--sold-out');
        } else {
          priceEl.classList.remove('price--sold-out');
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        const localVariant = this.findLocalVariant(selectedOptionValues);
        if (localVariant) {
          this.updatePrice(localVariant);
          this.updateVariantInputs(localVariant.id, localVariant.available);
          this.updateButtonState(localVariant.available);
          this.updateStockUrgency(localVariant.id, localVariant.available);
          this.updateSku(localVariant);
          if (localVariant.featured_media || localVariant.featured_image) {
            const mediaId = localVariant.featured_media?.id || localVariant.featured_image?.id;
            this.querySelector(`media-gallery`)?.setActiveMedia?.(`${this.dataset.section}-${mediaId}`, true);
            this.scrollToMedia();
          }
        }

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, localVariant?.id, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          selectedOptionValues,
          localVariant,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, localVariant),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, selectedOptionValues, localVariant, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error('Fetch error handled gracefully:', error);
              const currentVariant = localVariant || this.findLocalVariant(selectedOptionValues);
              if (currentVariant) {
                this.updatePrice(currentVariant);
                this.updateVariantInputs(currentVariant.id, currentVariant.available);
                this.updateButtonState(currentVariant.available);
                this.updateStockUrgency(currentVariant.id, currentVariant.available);
                this.updateSku(currentVariant);
              }
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, variantId, optionValues, shouldFetchFullPage = false) {
        const params = [];

        if (!shouldFetchFullPage) params.push(`section_id=${this.sectionId}`);

        if (variantId) {
          params.push(`variant=${variantId}`);
        } else if (optionValues && optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl, localVariant) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const serverVariant = this.getSelectedVariant(html);
          const variant = localVariant || serverVariant;

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant || serverVariant, sourceVariantSelects);

          if (variant) {
            this.pickupAvailability?.update(variant);
            this.updateOptionValues(html);
            this.updateURL(productUrl, variant.id);
            this.updateVariantInputs(variant.id, variant.available);
            this.updateMedia(html, variant.featured_media?.id || variant.featured_image?.id);

            const updateSourceFromDestination = (id, shouldHide = () => false) => {
              const source = html.getElementById(`${id}-${this.sectionId}`) || 
                             html.getElementById(`${id}-${this.dataset.section}`) ||
                             html.querySelector(`[id^="${id}-"]`);
              const destination = this.querySelector(`#${id}-${this.dataset.section}`) ||
                                  this.querySelector(`#${id}-${this.sectionId}`) ||
                                  document.getElementById(`${id}-${this.dataset.section}`) ||
                                  document.getElementById(`${id}-${this.sectionId}`) ||
                                  this.querySelector(`[id^="${id}-"]`) ||
                                  document.querySelector(`[id^="${id}-${this.dataset.section}"]`);
              if (source && destination) {
                destination.innerHTML = source.innerHTML;
                if (source.className) destination.className = source.className;
                destination.classList.toggle('hidden', shouldHide(source));
              }
            };

            updateSourceFromDestination('price');
            this.updatePrice(variant);
            updateSourceFromDestination('SkuNumber');
            updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
            updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
            updateSourceFromDestination('Volume');
            updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

            this.updateQuantityRules(this.sectionId, html);
            this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
            this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

            this.updateButtonState(variant.available);
            this.updateStockUrgency(variant.id, variant.available);
            this.updateSku(variant);
          } else {
            this.setUnavailable();
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId, isAvailable = true) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId ?? '';
            input.disabled = !isAvailable;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery');
        const mediaGalleryDestination = html.querySelector('media-gallery');

        if (mediaGallerySource && mediaGalleryDestination) {
          // Update main gallery viewer list
          const sourceViewerList = mediaGallerySource.querySelector('[id^="Slider-Gallery"]');
          const destViewerList = mediaGalleryDestination.querySelector('[id^="Slider-Gallery"]');
          if (sourceViewerList && destViewerList) {
            sourceViewerList.innerHTML = destViewerList.innerHTML;
          }

          // Update thumbnails list
          const sourceThumbList = mediaGallerySource.querySelector('[id^="Slider-Thumbnails"]');
          const destThumbList = mediaGalleryDestination.querySelector('[id^="Slider-Thumbnails"]');
          if (sourceThumbList && destThumbList) {
            sourceThumbList.innerHTML = destThumbList.innerHTML;
          }

          // Re-bind thumbnail click listeners
          if (typeof mediaGallerySource.bindThumbnailEvents === 'function') {
            mediaGallerySource.bindThumbnailEvents();
          }

          // Reset slider pagination on viewer & thumbnails
          const viewerComp = mediaGallerySource.querySelector('[id^="GalleryViewer"]');
          if (viewerComp && typeof viewerComp.resetPages === 'function') viewerComp.resetPages();
          const thumbComp = mediaGallerySource.querySelector('[id^="GalleryThumbnails"]');
          if (thumbComp && typeof thumbComp.resetPages === 'function') thumbComp.resetPages();

          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
        }

        // set featured media as active in the media gallery
        const targetMediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;
        this.querySelector('media-gallery')?.setActiveMedia?.(targetMediaId, true);

        // update media modal
        const modalContent = this.productModal?.querySelector('.product-media-modal__content');
        const newModalContent = html.querySelector('product-modal .product-media-modal__content');
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
